import React from "react";

/**
 * Aussie-style number plate for the "License to Love" landing.
 * Stylized (not exact reproductions) — each state has its own signature palette + slogan.
 */
const STATE_STYLES = {
  NSW: { bg: "#FFCB05", text: "#111", accent: "#111", label: "NEW SOUTH WALES", slogan: "The First State" },
  VIC: { bg: "#FFFFFF", text: "#003D7A", accent: "#003D7A", label: "VICTORIA", slogan: "The Place to Be" },
  QLD: { bg: "#FFFFFF", text: "#A5153F", accent: "#A5153F", label: "QUEENSLAND", slogan: "The Sunshine State" },
  WA:  { bg: "#FFFFFF", text: "#111",    accent: "#111",    label: "WESTERN AUSTRALIA", slogan: "The Golden State" },
  SA:  { bg: "#0E1214", text: "#F2EBD8", accent: "#D4A574", label: "SOUTH AUSTRALIA", slogan: "The Festival State" },
  TAS: { bg: "#FFFFFF", text: "#005F3C", accent: "#005F3C", label: "TASMANIA", slogan: "Explore the Possibilities" },
  ACT: { bg: "#FFFFFF", text: "#0057B7", accent: "#0057B7", label: "AUSTRALIAN CAPITAL TERR.", slogan: "The Nation's Capital" },
  NT:  { bg: "#A85B2A", text: "#F2EBD8", accent: "#F2EBD8", label: "NORTHERN TERRITORY", slogan: "Outback Australia" },
};

export function NumberPlate({ state = "VIC", plate = "LTL·001", onClick, size = "md", asButton = true }) {
  const s = STATE_STYLES[state] || STATE_STYLES.VIC;
  const dims = size === "lg"
    ? { w: 320, h: 116, plateFs: 44, labelFs: 10, sloganFs: 9 }
    : size === "sm"
      ? { w: 220, h: 80, plateFs: 30, labelFs: 8, sloganFs: 7 }
      : { w: 260, h: 96, plateFs: 36, labelFs: 9, sloganFs: 8 };

  const Wrapper = asButton ? "button" : "div";

  return (
    <Wrapper
      type={asButton ? "button" : undefined}
      onClick={onClick}
      data-testid={`plate-${state}`}
      className={`group relative shrink-0 rounded-[12px] transition-all duration-500 ${
        asButton ? "cursor-pointer hover:-translate-y-1 hover:rotate-[0.5deg] hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-ivy" : ""
      }`}
      style={{
        width: dims.w,
        height: dims.h,
        background: s.bg,
        color: s.text,
        border: `3px solid ${s.accent}`,
        boxShadow: "0 6px 16px rgba(14,18,20,0.18), inset 0 0 0 2px rgba(255,255,255,0.35)",
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      }}
      aria-label={asButton ? `Choose ${s.label}` : undefined}
    >
      {/* Top state banner */}
      <div
        className="absolute top-1 left-2 right-2 text-center tracking-[0.24em] font-bold uppercase"
        style={{ fontSize: dims.labelFs, color: s.text, opacity: 0.9 }}
      >
        {s.label}
      </div>

      {/* Main plate number */}
      <div
        className="absolute inset-0 flex items-center justify-center font-extrabold tracking-[0.14em]"
        style={{
          fontSize: dims.plateFs,
          color: s.text,
          textShadow: "0 1px 0 rgba(255,255,255,0.25)",
          marginTop: 4,
        }}
      >
        {plate}
      </div>

      {/* Bottom slogan */}
      <div
        className="absolute bottom-1 left-2 right-2 text-center italic"
        style={{ fontSize: dims.sloganFs, color: s.text, opacity: 0.75, fontFamily: "Fraunces, serif" }}
      >
        {s.slogan}
      </div>

      {/* Fake rivets */}
      <span className="absolute top-1.5 left-1.5 w-1.5 h-1.5 rounded-full" style={{ background: s.accent, opacity: 0.35 }} />
      <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full" style={{ background: s.accent, opacity: 0.35 }} />
      <span className="absolute bottom-1.5 left-1.5 w-1.5 h-1.5 rounded-full" style={{ background: s.accent, opacity: 0.35 }} />
      <span className="absolute bottom-1.5 right-1.5 w-1.5 h-1.5 rounded-full" style={{ background: s.accent, opacity: 0.35 }} />
    </Wrapper>
  );
}

export const AUSTRALIAN_STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];
